import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Repeat, TrendingUp, ShieldCheck, ArrowLeft, ArrowUpRight, Clock, XCircle, ExternalLink, CreditCard, Car,
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import VehiclePicker from '@/components/VehiclePicker';
import PlanStatusCard from '@/components/PlanStatusCard';
import { api } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { TID } from '@/testIds';

const fmtUSD = (n) => (Number(n) || 0).toLocaleString('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 2,
});

const statusStyle = (s) => {
  if (s === 'active' || s === 'trialing') return 'bg-[#E63946]/15 border-[#E63946] text-[#ff8c94]';
  if (s === 'past_due') return 'bg-yellow-500/10 border-yellow-500/40 text-yellow-300';
  if (s === 'canceled' || s === 'incomplete_expired') return 'bg-white/5 border-white/15 text-white/45';
  return 'bg-white/5 border-white/15 text-white/60';
};

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get('/dashboard/subscriptions')
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load subscriptions'))
      .finally(() => setLoading(false));
  }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post('/dashboard/portal', { return_url: window.location.href });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not open billing portal');
      setPortalLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-28 pb-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <Link to="/" className="mi-eyebrow text-white/50 hover:text-white inline-flex items-center gap-2">
              <ArrowLeft className="w-3 h-3" /> BACK TO HOME
            </Link>
            <div className="flex items-center gap-4">
              <div className="mi-eyebrow text-white/60 text-[10px]">
                SIGNED IN AS · <span className="text-white">{user?.email}</span>
              </div>
              <button onClick={logout} data-testid={TID.authLogoutBtn} className="mi-btn-ghost text-xs py-2 px-3">
                <XCircle className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
          </div>

          <div className="mb-10">
            <div className="mi-eyebrow mb-2 flex items-center gap-2 text-[#E63946]">
              <Repeat className="w-3 h-3" /> // SUBSCRIBER DASHBOARD · AFTERCARE MRR
            </div>
            <h1 className="mi-headline text-5xl md:text-6xl">
              Your <span className="text-[#E63946]">recurring revenue.</span>
            </h1>
            <p className="mt-4 text-white/60 max-w-2xl text-sm leading-relaxed">
              Every wrap you've enrolled in aftercare shows below. Wholesale MRR is what Motor iNKS bills you every
              month; your retail markup on top of these numbers is pure margin to your shop.
            </p>
          </div>

          {/* Stat tiles */}
          <div className="mb-8"><PlanStatusCard /></div>
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <StatTile
              label="MONTHLY WHOLESALE MRR"
              value={loading ? '—' : fmtUSD(data?.monthly_mrr ?? 0)}
              highlight testid={TID.dashMonthlyMrr}
            />
            <StatTile
              label="ACTIVE SUBSCRIPTIONS"
              value={loading ? '—' : String(data?.active_count ?? 0)}
              testid={TID.dashActiveCount}
            />
            <StatTile
              label="ANNUALIZED WHOLESALE"
              value={loading ? '—' : fmtUSD(data?.yearly_mrr ?? 0)}
              testid={TID.dashYearlyMrr}
            />
          </div>

          {/* Empty state / table */}
          {!loading && (!data?.subscriptions?.length) ? (
            <div className="mi-surface p-10 text-center">
              <Repeat className="w-10 h-10 text-[#E63946] mx-auto mb-4" />
              <div className="mi-headline text-2xl mb-2">No aftercare enrollments yet.</div>
              <p className="text-white/60 text-sm mb-6 max-w-md mx-auto">
                Enroll your first wrap into an aftercare plan and it'll start showing MRR here immediately.
              </p>
              <Link to="/aftercare" className="mi-btn-red">
                View aftercare plans <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="mi-surface overflow-hidden">
              <div className="px-6 py-4 border-b mi-line-strong flex items-center justify-between">
                <div className="mi-eyebrow">// RECENT SUBSCRIBER ACTIVITY</div>
                <div className="mi-eyebrow text-white/40">{data?.subscriptions?.length || 0} TOTAL</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] mi-eyebrow text-white/50 bg-black/40">
                      <th className="text-left px-6 py-3">SKU / PLAN</th>
                      <th className="text-left px-6 py-3">STATUS</th>
                      <th className="text-right px-6 py-3">QTY</th>
                      <th className="text-right px-6 py-3">MRR (WHOLESALE)</th>
                      <th className="text-left px-6 py-3">RENEWS</th>
                      <th className="text-left px-6 py-3 font-mono">SUB ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.subscriptions || []).map((s) => (
                      <tr key={s.subscription_id} className="border-t mi-line hover:bg-white/[0.02]">
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[#E63946] text-xs font-bold">{s.sku}</span>
                            <span className="text-white/85">{s.plan_name}</span>
                          </div>
                          <div className="mi-eyebrow text-[9px] text-white/40 mt-0.5">{s.cadence}</div>
                        </td>
                        <td className="px-6 py-3">
                          <span className={`inline-block px-2 py-0.5 border text-[10px] uppercase tracking-widest font-bold ${statusStyle(s.status)}`}>
                            {s.status}
                          </span>
                          {s.cancel_at_period_end && (
                            <div className="mi-eyebrow text-[9px] text-yellow-400/80 mt-1">CANCELS AT PERIOD END</div>
                          )}
                        </td>
                        <td className="px-6 py-3 text-right font-mono">×{s.quantity}</td>
                        <td className="px-6 py-3 text-right font-mono text-white font-semibold">{fmtUSD(s.wholesale_mrr)}</td>
                        <td className="px-6 py-3 text-white/60 text-xs">
                          {s.current_period_end
                            ? new Date(s.current_period_end * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> —</span>}
                        </td>
                        <td className="px-6 py-3 font-mono text-white/40 text-[10px]">{s.subscription_id.slice(0, 18)}…</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Vehicle sqft lookup (PVO database) */}
          <div className="mt-8 mi-surface p-6" data-testid="dash-vehicle-lookup">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
              <div>
                <div className="mi-eyebrow text-[#E63946] flex items-center gap-2"><Car className="w-3 h-3" /> // VEHICLE SQFT LOOKUP · PVO DATABASE</div>
                <div className="text-white/60 text-xs mt-1">Year → Make → Model panel dimensions for print planning. Toggle roof on / off to get your media order right.</div>
              </div>
              <Link to="/#wrapulator" className="mi-btn-ghost text-xs">
                Quote it in the Wrapulator <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <VehiclePicker testIdPrefix="dash-vp" />
          </div>

          {/* Footer helper */}
          <div className="mt-8 mi-surface p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-[#E63946] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-white/70 leading-relaxed">
                Update your card, download invoices, or cancel any subscription — all directly in Stripe.
                Need us? <a href="mailto:sales@motorinks.com" className="text-[#E63946]">sales@motorinks.com</a>.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={openPortal}
                disabled={portalLoading || !data?.total_count}
                data-testid="dashboard-portal-btn"
                className="mi-btn-red text-xs disabled:opacity-50"
                title={!data?.total_count ? 'Enroll at least one wrap first' : 'Open Stripe billing portal'}
              >
                <CreditCard className="w-3.5 h-3.5" />
                {portalLoading ? 'Opening…' : 'Manage in Stripe'}
              </button>
              <Link to="/aftercare" className="mi-btn-ghost text-xs">
                <TrendingUp className="w-3.5 h-3.5" /> Enroll another wrap
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function StatTile({ label, value, highlight, testid }) {
  return (
    <div className={`p-6 border ${highlight ? 'border-[#E63946] bg-[#E63946]/[0.05]' : 'mi-line-strong bg-[#0A0A0A]'}`}>
      <div className="mi-eyebrow text-[10px] text-white/50 mb-3">{label}</div>
      <div data-testid={testid} className="mi-headline text-5xl text-white leading-none">{value}</div>
    </div>
  );
}
